/**
 * ContratosAFacturarModal (v1.5.0-rc10) — vista previa de facturas que
 * se emitirán en la próxima corrida del poller de auto-facturación.
 *
 * Disparado desde la acción rápida "Auto-facturación" del dashboard.
 *
 * Llama a `GET /api/v1/dashboard/contratos-a-facturar?dias_anticipacion=N`
 * que simula el ciclo de mañana (hoy + 1) y enriquece con cliente, plan,
 * y monto estimado.
 *
 * No emite nada — solo informa. La ejecución manual vive en
 * `/ajustes/facturacion#auto-facturacion` (botón "Ejecutar ciclo manual").
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  FileText,
  Loader2,
  Zap,
} from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { apiCall } from "@/lib/api";
import { formatMXN } from "@/lib/utils";

type ContratoAFacturar = {
  contrato_id: string;
  codigo: string;
  cliente_id: string;
  cliente_codigo: string;
  cliente_nombre: string;
  plan_id: string | null;
  plan_nombre: string;
  monto_estimado: string;
  vencimiento: string;
  periodo_inicio: string;
  periodo_fin: string;
  dias_restantes_al_corte: number;
};

type Response = {
  dias_anticipacion: number;
  preview_para: string;
  total: number;
  monto_total_estimado: string;
  items: ContratoAFacturar[];
};

export function ContratosAFacturarModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // dias_anticipacion ajustable — null = usa default global del config.
  const [diasAnticipacion, setDiasAnticipacion] = useState<number | null>(null);

  const { data, isLoading, isError } = useQuery<Response>({
    queryKey: ["contratos-a-facturar", diasAnticipacion],
    queryFn: () => {
      const qs = diasAnticipacion !== null ? `?dias_anticipacion=${diasAnticipacion}` : "";
      return apiCall<Response>("get", `/api/v1/dashboard/contratos-a-facturar${qs}`);
    },
    enabled: open,
    refetchInterval: open ? 60_000 : false,
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()} size="xl">
      {/* Header — Dialog provee la X built-in en la esquina superior derecha */}
      <div className="flex items-center justify-between border-b p-4 pr-12">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-sky-100 dark:bg-sky-500/15 flex items-center justify-center">
            <Zap size={18} className="text-sky-700 dark:text-sky-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold">Próxima auto-facturación</h2>
            <p className="text-xs text-muted-foreground">
              {data
                ? `${data.total} contrato${data.total === 1 ? "" : "s"} · anticipación ${data.dias_anticipacion}d · total ${formatMXN(Number(data.monto_total_estimado))} · preview para ${formatPreviewDate(data.preview_para)}`
                : "cargando vista previa…"}
            </p>
          </div>
        </div>
      </div>

      {/* Filtro de anticipación. Cambiar dispara refetch via queryKey. */}
      <div className="border-b px-4 py-2.5 flex items-center gap-3 bg-muted/30 flex-wrap">
        <span className="text-xs text-muted-foreground">Simular con anticipación:</span>
        {[null, 1, 3, 5, 7, 10].map((d) => (
          <button
            key={String(d)}
            onClick={() => setDiasAnticipacion(d)}
            className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
              diasAnticipacion === d
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-border hover:bg-accent"
            }`}
          >
            {d === null ? "Default" : `${d} día${d === 1 ? "" : "s"}`}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            <Loader2 size={20} className="mx-auto mb-2 animate-spin" />
            calculando…
          </div>
        ) : isError ? (
          <div className="py-12 text-center text-sm text-destructive">
            Error al cargar — intenta refrescar
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-12 text-center">
            <CheckCircle2 size={32} className="mx-auto mb-3 text-emerald-500" />
            <p className="text-sm font-medium">Nada por facturar mañana</p>
            <p className="text-xs text-muted-foreground mt-1">
              Ningún contrato entra al ciclo del{" "}
              {data ? formatPreviewDate(data.preview_para) : "día siguiente"} con
              anticipación {data?.dias_anticipacion ?? "default"}d.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Cliente</th>
                <th className="text-left px-3 py-2 font-medium">Plan</th>
                <th className="text-right px-3 py-2 font-medium">Monto</th>
                <th className="text-center px-3 py-2 font-medium">Vence</th>
                <th className="text-center px-3 py-2 font-medium">Corte</th>
                <th className="text-right px-4 py-2 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr
                  key={it.contrato_id}
                  className="border-t hover:bg-accent/30 transition-colors"
                >
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/clientes/${it.cliente_id}`}
                      className="hover:underline"
                    >
                      <div className="font-medium">{it.cliente_nombre}</div>
                      <div className="text-[10px] text-muted-foreground tabular-nums">
                        {it.cliente_codigo} · {it.codigo}
                      </div>
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">
                    {it.plan_nombre || "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">
                    {formatMXN(Number(it.monto_estimado))}
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs tabular-nums">
                    {formatShortDate(it.vencimiento)}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span
                      className={`tabular-nums text-xs font-semibold ${
                        it.dias_restantes_al_corte <= 1
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-foreground"
                      }`}
                    >
                      {it.dias_restantes_al_corte === 0
                        ? "hoy"
                        : it.dias_restantes_al_corte === 1
                        ? "1d"
                        : `${it.dias_restantes_al_corte}d`}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/contratos/${it.contrato_id}`}
                        title="Ver contrato"
                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                      >
                        <FileText size={13} />
                      </Link>
                      <Link
                        to={`/facturas?cliente_id=${it.cliente_id}`}
                        title="Ver facturas del cliente"
                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                      >
                        <Calendar size={13} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="border-t p-3 flex items-center justify-between bg-muted/30">
        <p className="text-[11px] text-muted-foreground">
          Esta vista no emite nada. Para ejecutar manualmente, usa{" "}
          <Link to="/ajustes/facturacion#auto-facturacion" className="underline hover:text-foreground">
            /ajustes
          </Link>
          .
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          {data && data.total > 0 && (
            <Link to="/ajustes/facturacion#auto-facturacion" onClick={onClose}>
              <Button size="sm" className="gap-1">
                Ir a ajustes
                <ArrowRight size={12} />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </Dialog>
  );
}

// Helpers
function formatPreviewDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("es-MX", {
    weekday: "long",
    day: "2-digit",
    month: "short",
  });
}

function formatShortDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
}
